import { Component, OnInit, NgZone, ViewChild, ElementRef, ComponentFactoryResolver, ComponentRef, ViewContainerRef, Output, EventEmitter, HostListener } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material';
import { Observable, Subscription } from 'rxjs/Rx';
import 'rxjs/add/operator/debounceTime';
import * as moment from 'moment';
import { UUID } from 'angular2-uuid';
import { Location } from '@angular/common';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';
import { ValidationErrors } from '@angular/forms';

import { AsperaService } from '../aspera/aspera.service';
import { AuthService } from '../auth/auth.service'
import { Transfer } from './transfer.model';
import { TransferFile } from './transfer-file.model';
import { TransfersService } from './transfers.service';
import { UsersService } from '../users/users.service';
import { SuccessDialogComponent } from '../shared/success-dialog.component';
import { ErrorDialogComponent } from '../shared/error-dialog.component';
import { ListItemAnimation } from '../shared/list-item.animation';
import { TemplateSelectComponent } from '../templates/template-select.component';
import { Template } from '../templates/template.model';
import { Workflow } from '../workflows/workflow.model';
import { DynamicSectionsDirective } from '../forms/dynamic-sections.directive';
import { DynamicOverlaysDirective } from '../forms/dynamic-overlays.directive';
import { FormSectionComponent } from '../forms/form-section.component';
import { DefaultWorkflow } from '../workflows/default-workflow';
import { User } from '../users/user.model';
import { CollapseHeightAnimation } from '../shared/collapse-height.animation';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { UnsavedDialogComponent } from './unsaved-dialog.component';
import { TemplatesService } from '../templates/templates.service';
import { SaveAsDialogComponent } from './save-as-dialog.component';
import { ComponentCanDeactivate } from './unsaved-guard.service';
import { SaveDialogComponent } from './save-dialog.component';
import { WorkflowNavigationComponent } from '../workflows/workflow-navigation.component';
import { FadeStateAnimation } from '../shared/fade-state.animation';
import { RenameDialogComponent } from './rename-dialog.component';
import { RestoreDialogComponent } from './restore-dialog.component';
import { SendEmailDialogComponent } from './send-email-dialog.component';
import { DistributionList } from '../users/distribution-list.model';
import { FieldComponent, UndoAction } from '../forms/field.component';
import { UndoDialogComponent } from './undo-dialog.component';
import { DialogFadeAnimation } from '../shared/dialog-fade.animation';
import { StickyHeaderComponent } from '../shared/sticky-header.component';
import { QCSubmitDialogComponent } from '../qc/qc-submit-dialog.component';

export interface Section {
  id?: string,
  instance: FormSectionComponent,
  ref: ComponentRef<FormSectionComponent>,
}

@Component({
  selector: 'app-new-transfer',
  templateUrl: './new-transfer.component.html',
  styleUrls: ['./new-transfer.component.scss'],
  animations: [ListItemAnimation(), CollapseHeightAnimation, FadeInAnimation, FadeStateAnimation, DialogFadeAnimation]
})
export class NewTransferComponent implements OnInit, ComponentCanDeactivate {
  @Output() canNavigate = new EventEmitter<{enabled: boolean, action?: any}>();
  @Output() canSave = new EventEmitter<{enabled: boolean, action?: any}>();
  @Output() headerHiddenChanged = new EventEmitter<{hidden: boolean}>();
  @ViewChild(DynamicSectionsDirective) dynamicSections: DynamicSectionsDirective;
  @ViewChild(DynamicOverlaysDirective) dynamicOverlays: DynamicOverlaysDirective;
  @ViewChild("submitButton") submitButton;
  @ViewChild("submitButtonBadge") submitButtonBadge: ElementRef;
  @ViewChild("saveButton") saveButton;
  @ViewChild("saveCopyButton") saveCopyButton;
  @ViewChild("pdfLink") pdfLink: ElementRef;
  @ViewChild("headerElement") headerElement: ElementRef;
  @ViewChild("undoTemplate") undoTemplate: TemplatePortalDirective;
  @ViewChild("errorsTemplate") errorsTemplate: TemplatePortalDirective;

  sections: Section[] = [];
  transfer: Transfer = new Transfer();
  loading: boolean = true;
  submitting: boolean = false;
  get readyForSubmit(): boolean {
    return !this.submitting && this.valid
  }
  get qcReadyForSubmit(): boolean {
    return !this.submitting && this.template != null && this.valid
  }
  get reportSubmitted(): boolean {
    let type = this.workflow != null ? this.workflow.type : null;
    let submitted = this.template != null ? (this.template.submittedDate != null) : false;
    return (type == 'QC' || type == 'FORMAT') && submitted
  }
  asperaRunning: boolean = this.asperaService.running;
  currentUser: User = this.authService.currentUser;
  signedIn: boolean = this.authService.signedIn;
  cookie: string;
  template: Template;
  templateId: number;
  templates: Template[] = [];
  workflow: Workflow;
  workflowId: number;
  workflows: Workflow[] = [];
  favoriteTemplateIds: number[];
  archivingTemplate: boolean = false;
  profileLoading: boolean = true;
  profileLoaded: boolean = false;
  workflowLoading: boolean = false;
  transferLoading: boolean = false;
  draftTemplate: Template;
  canCreateTemplates: boolean = false;
  defaultWorkflow: Workflow = DefaultWorkflow();
  lists: number[] = [];
  uploadPackageGuid: string;
  pdfSubscription: Subscription;
  pdfLoading: boolean = false;
  pdfSaving: boolean = false;
  previousTemplate: Template;
  previousVersion: any;
  previousVersionSubscription: Subscription;
  previousVersionLoading: boolean = false;
  navigationSections = [];
  header: any;
  headerHidden: boolean = false;
  headerValues = {};
  headerState: string = "inactive";
  sectionsLoaded: number = 0;
  standard: any;
  stickyHeaders: StickyHeaderComponent[] = [];
  sending: boolean = false;
  sendingSubscription: Subscription;
  viewingChanges: boolean = false;
  undoActions: UndoAction[] = [];
  undoOverlayRef: OverlayRef;
  undoDescription: string;
  undoDialogState: string = "closed";
  undoDialogTimeout: any;
  templateSaving: boolean = false;
  autosaveTimeout: any;
  routeUpdating: boolean = false;
  valid: boolean = false;
  validations: any;
  errors: any = {};
  atLeastOneRequiredFields: {[id: string]: FieldComponent} = {};
  errorsOverlayRef: OverlayRef;

  get errorDescriptions(): string[] {
    let descriptions: string[] = [];
    for (let key in this.errors) {
      let value = this.errors[key];
      let description = this.descriptionForError(key, value);
      descriptions.push(description != null ? description : 'Workflow is invalid')
    }
    return descriptions;
  }

  get monorailWorkflow(): boolean {
    return this.workflow != null && this.workflow.type == 'MONORAIL'
  }
  get templateEditable(): boolean {
    return this.canCreateTemplates && (this.template == null || this.template.canModify)
  }
  get templateLockable(): boolean {
    return this.monorailWorkflow && this.template != null && this.templateEditable && this.workflow != this.defaultWorkflow
  }

  constructor(
    private asperaService: AsperaService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private templatesService: TemplatesService,
    private transfersService: TransfersService,
    private usersService: UsersService,
    public dialog: MatDialog,
    private ngZone: NgZone,
    private resolver: ComponentFactoryResolver,
    private location: Location,
    private overlay: Overlay) { }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      this.template = null;
      this.workflow = null;
      this.uploadPackageGuid = params.p;
      this.templateId = params.t != null ? +params.t : null;
      this.workflowId = params.w != null ? +params.w : null;
      this.loadProfile();
    });

    Observable.fromEvent(window, 'scroll').subscribe(
      () => this.onScroll()
    )

    Observable.fromEvent(document, 'keydown').subscribe(
      (event: KeyboardEvent) => this.onKeydown(event)
    )
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.keyCode) {
    case 8: // backspace
      let tag = document.activeElement.tagName.toLowerCase();
      if (tag != 'input' && tag != 'textarea') {
        event.preventDefault();
      }
      break;
    case 90: // z
      if (event.metaKey || event.ctrlKey) {
        if (this.undoActions.length > 0) {
          let tag = document.activeElement.tagName.toLowerCase();
          if (tag != 'input' && tag != 'textarea') {
            event.preventDefault();
            this.undo();
          }
        }
      }
      break;
    }
    return true;
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.draftTemplate != null && this.draftTemplate.dirty) {
      return this.showUnsavedDialog();
    } else {
      return true;
    }
  }

  updateRoute() {
    this.routeUpdating = true;
    let query: any = {};
    if (this.workflow != null) {query.w = this.workflow.id};
    if (this.template != null) {query.t = this.template.id};
    this.router.navigate(["/home"], {queryParams: query});
  }

  loadProfile() {
    if (this.profileLoaded == true) {
      this.loadParams();
      return;
    }
    this.authService.getUser().subscribe((success) => {
      this.usersService.profile.subscribe(
        (profile) => {
          this.workflows = profile.workflows;
          this.templates = profile.templates;
          this.favoriteTemplateIds = profile.favoriteTemplateIds;
          this.canCreateTemplates = profile.canCreateTemplates;
          this.profileLoading = false;
          this.profileLoaded = true;
          this.loadParams();
        },
        (error) => this.profileLoading = false
      )
    })
  }

  loadParams() {
    if (this.uploadPackageGuid != null) {
      this.loadTransfer(this.uploadPackageGuid);
    } else {
      this.checkDraft();
    }
  }

  loadInitialState() {
    if (this.templateId != null) {
      this.templatesService.getTemplate(this.templateId).subscribe(
        (template) => {
          this.template = template;
          this.workflow = this.workflows.find((w) => w.id === template.workflowId);
          this.loadWorkflow();
        },
        (error) => {
          this.workflow = this.defaultWorkflow;
          this.loadWorkflow();
        }
      )
    } else if (this.workflowId != null) {
      this.workflow = this.workflows.find((w) => w.id === this.workflowId);
      this.loadWorkflow();
    } else {
      this.workflow = this.defaultWorkflow;
      this.loadWorkflow();
    }
  }

  loadAspera() {
    this.asperaService.startSession(() => {
      this.asperaRunning = this.asperaService.running;
    });
  }

  loadWorkflow() {
    this.routeUpdating = false;
    if (this.workflowLoading) { return }
    this.workflowLoading = true;
    this.canNavigate.emit({enabled: false});
    this.canSave.emit({enabled: false});
    if (this.autosaveTimeout != null) {
      clearTimeout(this.autosaveTimeout);
      this.autosaveTimeout = null;
    }
    let viewContainer = this.dynamicSections.viewContainerRef;
    viewContainer.clear();
    this.sections = [];
    this.sectionsLoaded = 0;
    this.navigationSections = [];
    this.header = null;
    this.headerValues = {};
    if (this.previousVersionSubscription != null) { this.previousVersionSubscription.unsubscribe() }
    this.previousVersionLoading = false;
    this.hideChanges();
    this.undoActions = [];
    this.stickyHeaders = [];
    this.atLeastOneRequiredFields = {};
    this.valid = false;
    this.errors = {};

    // Temporary work around for workflows with missing data
    if (this.workflow == null || this.workflow.sections == null) {
      this.workflow = this.defaultWorkflow;
      this.workflowId = this.workflow.id;
    }

    if (!this.asperaRunning && this.workflow.type == 'MONORAIL') {
      this.loadAspera();
    }

    this.resetTransfer();

    if (this.template != null && this.favoriteTemplateIds != null) {
      if (this.favoriteTemplateIds.find((t) => t === this.template.id)) {
        this.template.isFavorite = true;
      }
    }

    if (this.template != null && (this.workflow.type == "FORMAT" || this.workflow.type == "QC")) {
      let versions = this.template.data["_versions"];
      if (versions != null && versions.length > 0) {
        this.previousVersion = versions[versions.length-1];
      }
    }

    if (this.uploadPackageGuid == null && this.draftTemplate == null) {
      this.createDraft();
    }

    this.validations = this.workflow.validations;

    setTimeout(() => {
      if (this.workflow != null) {
        this.loadHeader();
        for (let section of this.workflow.sections) {
          this.loadSection(section);
        }
      }
    }, 500); // Giving Angular time to update the interface before loading
  }

  loadSection(section) {
    let factory = this.resolver.resolveComponentFactory(FormSectionComponent);
    let viewContainer = this.dynamicSections.viewContainerRef;
    let component = viewContainer.createComponent(factory);

    let element = component.location.nativeElement
    if (section.columns != null) {
      element.className = "form-section column-"+section.columns;
    } else {
      element.className = "form-section column-12";
    }

    let sectionId = UUID.UUID();

    let instance = component.instance;
    instance.id = sectionId;
    instance.viewLoaded.subscribe(() => this.onViewLoaded());
    instance.section = section;
    instance.locked = this.reportSubmitted;
    instance.lockValues = this.template != null && this.template.lockValues;
    instance.workflowId = this.workflow.id;
    if (this.template != null) {
      instance.templateData = this.template.data;
      instance.distributionLists = this.template.distributionLists;
      instance.recipients = this.template.recipients;
    }
    if (this.draftTemplate != null) {
      instance.draftData = this.draftTemplate.data;
    }
    instance.valueChanged.subscribe(
      (value) => this.valueChanged(value)
    )
    instance.disclaimerChanged.subscribe(
      (event) => this.onDisclaimerChanged(event)
    )
    instance.updateLinked.subscribe((event) => {
      for (let section of this.sections) {
        if (section.instance != instance) {
          section.instance.onUpdateLinked(event);
        }
      }
    })
    instance.rowLinkAdded.subscribe((event) => {
      for (let section of this.sections) {
        if (section.instance != instance) {
          section.instance.onRowLinkAdded(event);
        }
      }
    })
    instance.rowLinkRemoved.subscribe((event) => {
      for (let section of this.sections) {
        if (section.instance != instance) {
          section.instance.onRowLinkRemoved(event);
        }
      }
    })
    instance.rowLinkUpdated.subscribe((event) => {
      for (let section of this.sections) {
        if (section.instance != instance) {
          section.instance.onRowLinkUpdated(event);
        }
      }
    })
    instance.createFileSection.subscribe((event) => {
      this.createFileSection(event)
    })
    instance.removeFileSection.subscribe((event) => {
      this.removeFileSection(event)
    })
    instance.resized.subscribe(() => {
      this.onResize()
    })
    instance.dynamicOverlays = this.dynamicOverlays;
    instance.listAdded.subscribe((event) => this.onListAdded(event));
    instance.undoableAction.subscribe((action) => this.onUndoableAction(action));
    instance.stickyHeaderAdded.subscribe((header) => this.stickyHeaders.push(header));
    instance.standardChanged.subscribe((standard) => this.setStandard(standard));
    instance.standardConverted.subscribe((standard) => this.convertStandard(standard));
    instance.timecodesOffsetChanged.subscribe((event) => this.offsetTimecodesBy(event));
    instance.workflowValidations = this.validations;
    instance.atLeastOneRequiredFieldAdded.subscribe((field) => this.onAtLeastOneRequiredFieldAdded(field));

    this.sections.push({
      id: sectionId,
      instance: instance,
      ref: component
    });
  }

  removeSection(section: Section) {
    let index = this.sections.indexOf(section);
    this.sections.splice(index, 1);
    section.ref.destroy();
    setTimeout(() => this.onResize(), 100);
  }

  createFileSection(event: {fileId: string, section: any}) {
    let factory = this.resolver.resolveComponentFactory(FormSectionComponent);
    let viewContainer = this.dynamicSections.viewContainerRef;
    let component = viewContainer.createComponent(factory);

    let element = component.location.nativeElement
    element.className = "form-section column-12";

    let fileId = event.fileId;
    let sectionId = "f-" + fileId;

    let instance = component.instance;
    instance.id = sectionId;
    instance.section = event.section;
    instance.workflowId = this.workflow.id;
    instance.valueChanged.subscribe(
      (value) => this.fileValueChanged(fileId, value)
    )
    instance.viewLoaded.subscribe(
      () => this.onResize()
    )
    instance.resized.subscribe(
      () => this.onResize()
    )
    instance.dynamicOverlays = this.dynamicOverlays;

    this.sections.push({
      id: sectionId,
      instance: instance,
      ref: component
    });
  }

  removeFileSection(fileId) {
    let sectionId = "f-" + fileId;
    let section = this.sections.find((s) => s.id === sectionId);
    if (section != null) {
      this.removeSection(section);
    }
    if (this.draftTemplate != null) {
      let files = this.draftTemplate.data["_files"];
      if (files != null && files[fileId] != null) {
        delete files[fileId];
      }
    }
  }

  onViewLoaded() {
    this.sectionsLoaded += 1
    if (this.sectionsLoaded === this.workflow.sections.length) {
      setTimeout(() => this.onWorkflowLoaded(), 100);
    }
  }

  onWorkflowLoaded() {
    for (let section of this.sections) {
      section.instance.updateLinkedFields();
    }
    if (this.standard != null) {
      for (let section of this.sections) {
        section.instance.setStandard(this.standard);
      }
    }
    this.loadWorkflowNavigation();
    this.setHeaderHeight();
    this.workflowLoading = false;
  }

  onResize() {
    if (this.viewingChanges) {
      this.positionOverlays();
    }
    this.resetSticky();
  }

  onListAdded(listId) {
    if (this.lists.find((id) => id === listId) == null) {
      this.lists.push(listId);
      if (this.draftTemplate != null) {
        this.draftTemplate.data["_dl_ids"] = this.lists;
        localStorage.setItem('draftTemplate', JSON.stringify(this.draftTemplate));
      }
    }
  }

  positionOverlays() {
    for (let section of this.sections) {
      section.instance.positionOverlays();
    }
  }

  reset() {
    for (let section of this.sections) {
      section.instance.reset();
    }
    if (this.draftTemplate != null) {
      this.draftTemplate.dirty = false;
      this.canSave.emit({enabled: false});
    }
  }

  checkDirty() {
    this.draftTemplate.dirty = this.sections.filter((s) => s.instance.dirty).length > 0
  }

  resetDirty(templateData) {
    for (let section of this.sections) {
      section.instance.resetDirty(templateData);
    }
    if (this.draftTemplate != null) {
      this.checkDirty();
      this.canSave.emit({enabled: this.draftTemplate.dirty});
    }
  }

  valueChanged(event:{field: string, value: any}) {
    if (this.draftTemplate != null) {
      this.draftTemplate.data[event.field] = event.value;
      this.checkDirty();
      localStorage.setItem('draftTemplate', JSON.stringify(this.draftTemplate));
      if (this.template != null && !this.templateSaving && this.workflow.type == 'QC' && this.draftTemplate.dirty && this.autosaveTimeout == null) {
        this.autosaveTimeout = setTimeout(() => this.autosave(), 30000);
      }
    }

    if (this.headerValues.hasOwnProperty(event.field)) {
      this.headerValues[event.field] = this.displayValue(event.value)
    }

    this.validate();
    this.updateSubmitButton();
    if (this.draftTemplate != null && this.draftTemplate.dirty && (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT')) {
      this.canSave.emit({enabled: true, action: () => this.onSave()})
    }
  }

  fileValueChanged(fileId: string, event:{field: string, value: any}) {
    if (this.draftTemplate != null) {
      if (this.draftTemplate.data["_files_data"] == null) {
        this.draftTemplate.data["_files_data"] = {};
      }
      let files = this.draftTemplate.data["_files_data"];
      if (files[fileId] == null) { files[fileId] = {} }
      files[fileId][event.field] = event.value;
    }

    this.updateSubmitButton();
  }

  onDisclaimerChanged(event:{field: string, value: any}) {
    if (this.draftTemplate != null) {
      if (this.draftTemplate.data["_disclaimers"] == null) {
        this.draftTemplate.data["_disclaimers"] = {};
      }
      this.draftTemplate.data["_disclaimers"][event.field] = event.value;
    }
  }

  displayValue(value) {
    if (value == null) {
      return '';
    } else if (value.hasOwnProperty("displayed")) {
      return value.displayed;
    } else if (value instanceof Array) {
      return value.map((v) => this.displayValue(v)).join(', ');
    } else {
      return ''+value;
    }
  }

  metaValue(value) {
    if (value == null) {
      value = '';
    }
    if (value instanceof Array) {
      let values = []
      for (let v of value) {
        let mv = this.metaValue(v);
        if (mv instanceof Array) {
          values = values.concat(mv);
        } else {
          values.push(mv);
        }
      }
      return values;
    } else {
      if (value.hasOwnProperty("stored")) {
        if (value.stored.hasOwnProperty("users")) {
          return value.stored.users.map((u) => {
            return this.metaUserValue(u);
          })
        } else if (value.stored.hasOwnProperty("id")) {
          let user = value.stored;
          return this.metaUserValue(user);
        } else {
          let stored = value.stored != null ? value.stored : '';
          let displayed = value.displayed != null ? value.displayed : '';
          return {stored: stored, displayed: displayed};
        }
      } else {
        return {stored: value, displayed: value}
      }
    }
  }

  metaUserValue(user: User) {
    let stored = user.id != null ? user.id : '';
    let name = user.name != null ? user.name : user.email;
    let displayed = name != null ? name : '';
    return {stored: stored, displayed: displayed};
  }

  updateSubmitButton() {
    if (this.submitButton == null) { return }
    if (this.workflow.type === "QC" || this.workflow.type === "FORMAT") {
      this.submitButton.disabled = !this.qcReadyForSubmit;
    } else {
      this.submitButton.disabled = !this.readyForSubmit;
    }
  }

  setStandard(standard) {
    this.standard = standard;
    if (this.workflowLoading) { return }
    for (let section of this.sections) {
      section.instance.setStandard(standard);
    }
  }

  convertStandard(standard) {
    this.standard = standard;
    for (let section of this.sections) {
      section.instance.convertStandard(standard);
    }
  }

  offsetTimecodesBy(event: {offset: string, subtract: boolean}) {
    for (let section of this.sections) {
      section.instance.offsetTimecodesBy(event);
    }
  }

  onSubmit() {
    if (!this.readyForSubmit) {return}
    this.submitting = true;

    this.transfer.senderId = this.currentUser.id;
    this.transfer.senderEmail = this.currentUser.email;
    this.transfer.senderFirstName = this.currentUser.firstName;
    this.transfer.senderLastName = this.currentUser.lastName;

    let data = this.draftTemplate.data;

    // Combine bit rate + unit measurements upon submission
    if (data.video_bit_rate != null) {
      data.video_bit_rate = data.video_bit_rate + data.video_bit_rate_units.stored;
    }
    if (data.audio_bit_rate != null) {
      data.audio_bit_rate = data.audio_bit_rate + data.audio_bit_rate_units.stored;
    }
    if (data.audio_sample_rate != null) {
      data.audio_sample_rate = data.audio_sample_rate + data.audio_sample_rate_units.stored;
    }
    if (data.file_size != null) {
      data.file_size = data.file_size + data.file_units.stored;
    }

    if (this.workflow.hiddenFields != null) {
      for (let field of this.workflow.hiddenFields) {
        data[field.id] = field.defaultValue;
      }
    }

    if (data["files"] != null && data["_files_data"] != null) {
      let index = 1;
      for (let file of data["files"]) {
        data["filename_"+index] = file.name;
        let fileData = data["_files_data"][file.id];
        if (fileData != null) {
          for (let key in fileData) {
            let value = fileData[key];
            data[key+"_"+index] = value;
          }
        }
        index += 1;
      }
    }

    let metadata = {}
    for (let key in data) {
      if (key[0] === '_') { continue }
      let value = data[key];
      let metaKey = key;
      switch(key.toLowerCase()) {
      case "destinations":
        metaKey = "Destinations";
        break;
      case "senderemail":
        metaKey = "senderEmail";
        break;
      case "userdelivery":
        metaKey = "userDelivery";
        break;
      }
      let metaValue = this.metaValue(value);
      if (metaValue instanceof Array) {
        if (metaValue.length > 0) {
          metadata[metaKey] = metaValue;
        }
      } else {
        metadata[metaKey] = [metaValue];
      }
      switch(key.toLowerCase()) {
       case "delivery_notes":
         this.transfer.notes = value
         break;
       case "expiration_date":
        if (value != null) {
          let exp = moment(value, 'YYYY-MM-DD');
          this.transfer.expirationDate = exp;
          metadata[metaKey] = [this.metaValue(exp.format('MM/DD/YYYY'))];
        }
        break;
      case "files":
        this.transfer.files = value;
        delete metadata["files"];
        break;
      case "notifications":
      case "email":
        metaValue = []
        let emails: string[] = [];
        for (let v of value) {
          if (v.stored.users != null) {
            for (let u of v.stored.users) {
              emails.push(u.email);
              let name = u.name != null ? u.name : u.email;
              metaValue.push({displayed: name, stored: u.email});
            }
          } else {
            emails.push(v.stored);
            let name = v.name != null ? v.name : v.stored;
            metaValue.push({displayed: name, stored: v.stored});
          }
        }
        metadata[metaKey] = metaValue;
        this.transfer.notifications = emails;
        break;
      case "priority":
        let priority = value instanceof Array ? value[0] : value;
        this.transfer.priority = +priority.stored;
        break;
      case "recipients":
      case "userdelivery":
        let users: User[] = [];
        for (let v of value) {
          if (v.stored.users != null) {
            for (let u of v.stored.users) {
              users.push(u);
            }
          } else if (v.stored.email != null) {
            users.push(v.stored);
          } else {
            let user: any = {}
            user.id = v.stored;
            if (v.displayed != null) {
              let match = v.displayed.match(/^(.+) \(([^\(\)]*)\)$/)
              if (match != null) {
                user.firstName = match[1]
                user.lastName = "("+match[2]+")"
                user.email = match[2]
              }
            }
            users.push(new User(user));
          }
        }
        this.transfer.recipients = users;
        break;
      }
    }
    metadata["workflow_id"] = [this.metaValue(this.workflow.id)];
    let template_id = this.template != null ? this.template.id : '';
    metadata["template_id"] = [this.metaValue(template_id)];

    this.transfer.metadata = metadata;

    this.transfer.status = "running";
    if (this.transfer.workflowType === "FORM") {
      this.submitForm();
    } else {
      this.createPackage()
    }
  }

  showQcSubmitDialog() {
    if (this.draftTemplate.dirty) {
      if (this.draftTemplate.data.status != null &&
          this.draftTemplate.data.status.displayed === "Pass" &&
          this.draftTemplate.data.high_priority_notes != null &&
          this.draftTemplate.data.high_priority_notes.length > 0) {
        this.ngZone.run(() => {this.showErrorDialog("Cannot submit report with Pass status when there are High Priority Notes remaining.")});
        return;
      }
    } else if (this.template.data.status != null &&
              this.template.data.status.displayed === "Pass" &&
              this.template.data.high_priority_notes != null &&
              this.template.data.high_priority_notes.length > 0){
      this.ngZone.run(() => {this.showErrorDialog("Cannot submit report with Pass status when there are High Priority Notes remaining.")})
      return;
    }
    let recipients = []
    if (this.workflow.type === "FORMAT") {
      let list = {
        name: "Format Check Group",
        users: [
          "eli.selzer@disney.com",
          "myesha.t.mcandrew@disney.com",
          "john.b.corney@disney.com"
        ].map((u) => { return {email: u} })
      };
      recipients.push({displayed: list.name, stored: list});
    }

    let dialogRef = this.dialog.open(QCSubmitDialogComponent, {
      height: '280px',
      width: '600px',
      data: {
        reportName: this.draftTemplate.name,
        aspectRatio: (this.draftTemplate.data['pixel_aspect_ratio'] != null ? this.draftTemplate.data['pixel_aspect_ratio']['displayed'] : null),
        domesticEpisode: this.draftTemplate.data['domestic_episode_number'],
        elementType: (this.draftTemplate.data['element_type'] != null ? this.draftTemplate.data['element_type']['displayed'] : null),
        foreignEpisode: this.draftTemplate.data['foreign_episode_number'],
        format: (this.draftTemplate.data['format'] != null ? this.draftTemplate.data['format']['displayed'] : null),
        houseNumber: this.draftTemplate.data['house_number'],
        language: (this.draftTemplate.data['audio_language'] != null ? this.draftTemplate.data['audio_language']['displayed'] : null),
        programTitle: (this.draftTemplate.data['program_title'] != null ? this.draftTemplate.data['program_title']['displayed'] : null),
        revision: this.draftTemplate.data['revision'],
        standard: (this.draftTemplate.data['timecode_standard'] != null && this.draftTemplate.data['timecode_standard']['format'] != null ?  this.draftTemplate.data['timecode_standard']['format']['displayed'] : null),
        status: (this.draftTemplate.data['status'] != null ? this.draftTemplate.data['status']['displayed'] : null),
        version: (this.draftTemplate.data['program_version'] != null ? this.draftTemplate.data['program_version']['displayed'] : null),
        videoBitRate: this.draftTemplate.data['video_bit_rate'],
        videoBitRateUnits: (this.draftTemplate.data['video_bit_rate_units'] ?  this.draftTemplate.data['video_bit_rate_units']['displayed'] : null),
        recipients: recipients,
        workflowId: this.workflowId
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.draftTemplate.name = result.reportName;
        this.onQcSubmit(result.recipients);
      }
    })
  }

  onQcSubmit(recipients) {
    if (!this.qcReadyForSubmit) {return}
    this.submitting = true;

    this.transfer.senderId = this.currentUser.id;
    this.transfer.senderEmail = this.currentUser.email;
    this.transfer.senderFirstName = this.currentUser.firstName;
    this.transfer.senderLastName = this.currentUser.lastName;
    this.transfer.metadata = {};
    this.transfer.notifications = recipients;

    this.updateTemplate({success: () => this.submitQcReport()});
  }

  submitQcReport() {
    this.transfersService.createPackage(this.transfer).subscribe(
      (response) => {
        this.template.submittedDate = response.deliveryDate;
        let existingTemplate = this.templates.find((t) => t.id === this.template.id)
        if (existingTemplate != null) {
          let index = this.templates.indexOf(existingTemplate);
          this.templates[index] = this.template;
          this.usersService.updateProfileTemplates(this.templates);
        }
        this.loadWorkflow();
        this.submitting = false;
        this.ngZone.run(() => {this.showSuccessDialog("Report Successfully Sent")})
      },
      (errors) => {
        console.log("Report could not be sent"); console.log(errors);
        this.submitting = false;
        this.ngZone.run(() => {this.showErrorDialog("Failed to Send Report")})
      },
    )
  }

  submitForm() {
    this.transfersService.submitForm(this.transfer).subscribe(
      (response) => {
        this.removeDraft();
        this.createDraft();
        if (this.transfer.files.length > 0) {
          this.createPackage();
        } else {
          this.submitting = false;
          this.transfer.status = "pending";
          this.reset();
          this.ngZone.run(() => {this.showSuccessDialog("Form Successfully Sent")})
        }
      },
      (errors) => {
        this.transfer.status = "failed"
        this.submitting = false;
        this.ngZone.run(() => {this.showErrorDialog("Form Failed to Send")})
      },
    )
  }

  createPackage() {
    this.transfersService.createPackage(this.transfer).subscribe(
      (response) => {
        // console.log("Package created. Package id: " + response.packageId);
        this.transfer.uploadPackageId = response.packageId;
        this.transfer.uploadPackageGuid = response.packageGuid;
        this.transfer.deliveryDate = response.deliveryDate;
        this.transfer.files = response.files;
        this.onPackageCreated();
      },
      (errors) => {
        this.transfer.status = "failed"
        this.submitting = false;
        this.ngZone.run(() => {this.showErrorDialog("Files Failed to Upload")})
      },
    )
  }

  onPackageCreated() {
    this.resetDirty(this.draftTemplate.data);
    if (this.transfer.files.length === 0) {
      this.onTransferEnd('completed');
    } else {
      this.transfersService.transferFiles('upload', this.transfer.uploadPackageGuid, this.transfer.uploadPackageId, this.transfer.files)
        .subscribe(
          (response) => {
            this.cookie = response.cookie;
            this.transfer.requestId = response.requestId;
          },
          (errors) => {
            console.log('Transfer could not be started'); console.log(errors);
            this.onTransferEnd('failed');
          }
        );
    }
  }

  onTransferEnd(status: string) {
    // console.log("Transfer ended. Status: " + status);
    this.transfer.status = status;
    if (status === "completed") {
      for (let file of this.transfer.files) {
        file.status = 'completed';
      }
      this.ngZone.run(() => {this.showSuccessDialog("Files Successfully Sent")})
    } else {
      this.ngZone.run(() => {this.showErrorDialog("Files Failed to Send")})
    }
    this.submitting = false;
  }

  showSuccessDialog(message) {
    let dialogRef = this.dialog.open(SuccessDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 3000);
  }

  showErrorDialog(message) {
    let dialogRef = this.dialog.open(ErrorDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 6000);
  }

  showTemplatesDialog() {
    let dialogRef = this.dialog.open(TemplateSelectComponent, {
      height: Math.min(window.innerHeight, 582)+"px",
      width: Math.min(window.innerWidth, 600)+"px",
      data: {workflows: this.workflows}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.selectTemplate(result);
      }
    })
  }

  selectTemplate(event:{template: Template, workflow: Workflow}) {
    let template = event.template;
    let workflow = event.workflow;
    if (this.draftTemplate != null && this.draftTemplate.dirty) {
      this.showUnsavedDialog().subscribe((discard) => {
        if (discard) {this.loadTemplate(template, workflow)}
      });
    } else {
      this.loadTemplate(template, workflow)
    }
  }

  loadTemplate(template, workflow) {
    this.removeDraft();
    this.template = template;
    this.templateId = template != null ? template.id : null;
    this.workflow = workflow;
    this.workflowId = workflow != null ? workflow.id : null;
    this.uploadPackageGuid = null
    this.previousVersion = null;
    this.previousTemplate = null;
    this.updateRoute();
  }

  showUnsavedDialog(): Observable<boolean> {
    let dialogRef = this.dialog.open(UnsavedDialogComponent, {
      height: '240px',
      width: '320px',
      data: {
        copy: (this.draftTemplate.author.id !== this.currentUser.id),
        templateName: this.draftTemplate.name
      }
    });
    return dialogRef.afterClosed().map((result) => {
      if (result != null) {
        if (result.discard === true) {
          this.removeDraft();
          return true;
        } else {
          return false
        }
      }
    })
  }

  toggleFavorite(template) {
    if (this.favoriteTemplateIds == null) {return}

    template.isFavorite = !template.isFavorite;

    let favoriteIndex = this.favoriteTemplateIds.indexOf(template.id)
    if (favoriteIndex === -1 && template.isFavorite) {
      this.favoriteTemplateIds.push(template.id);
    } else if (favoriteIndex !== -1 && !template.isFavorite) {
      this.favoriteTemplateIds.splice(favoriteIndex, 1);
    }

    let existingTemplate = this.templates.find((t) => t.id === template.id)
    if (existingTemplate == null && template.isFavorite) {
      this.templates.push(template);
    } else if (existingTemplate != null) {
      if (template.isFavorite || template.isRecent) {
        existingTemplate.isFavorite = template.isFavorite;
      } else {
        let templateIndex = this.templates.indexOf(existingTemplate);
        this.templates.splice(templateIndex, 1);
      }
    }

    this.usersService.updateFavorites(this.favoriteTemplateIds, this.templates).subscribe()
  }

  checkDraft() {
    let storedDraft = localStorage.getItem('draftTemplate');
    if (storedDraft != null) {
      let draft = JSON.parse(storedDraft) as Template;
      if (draft.dirty) {
        let workflowType = "template";
        let storedWorkflow = localStorage.getItem('workflow')
        if (storedWorkflow != null) {
          let workflow = JSON.parse(storedWorkflow) as Workflow;
          if (workflow.type === "QC" || workflow.type === "FORMAT") {
            this.showRestoreDialog(workflowType)
            return;
          }
        }
      }
    }
    this.loadInitialState();
  }

  showRestoreDialog(workflowType) {
    let dialogRef = this.dialog.open(RestoreDialogComponent, {
      height: '240px',
      width: '320px',
      disableClose: true,
      data: {workflowType: workflowType}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.restore === true) {
        this.loadDraft();
      } else {
        this.removeDraft();
      }
      this.loadWorkflow();
    })
  }

  loadTransfer(uploadPackageGuid) {
    this.transferLoading = true;
    this.transfersService.getPackage(uploadPackageGuid).subscribe(
      (transfer) => {
        let data = transfer.metadata;
        if (data["expiration_date"] != null) {
          let now = moment();
          let expiration = moment(data["expiration_date"], 'MM/DD/YYYY')
          data["expiration_date"] = expiration.isValid() && expiration.isAfter(now) ? expiration.format('YYYY-MM-DD') : null;
        }
        this.workflowId = transfer.workflowId;
        this.templateId = transfer.templateId;
        this.createDraft()
        this.draftTemplate.data = data;
        this.loadInitialState();
        this.transferLoading = false;
      },
      (error) => {
        console.log("Could not load transfer");
        this.loadInitialState();
        this.transferLoading = false;
      }
    )
  }

  loadDraft() {
    let storedDraft = localStorage.getItem('draftTemplate')
    if (storedDraft != null) {
      let draft = JSON.parse(storedDraft) as Template;
      if (draft.dirty) {
        this.draftTemplate = draft;
        let storedTemplate = localStorage.getItem('template')
        if (storedTemplate != null) {
          this.template = JSON.parse(storedTemplate) as Template;
        }
        let storedWorkflow = localStorage.getItem('workflow')
        if (storedWorkflow != null) {
          this.workflow = JSON.parse(storedWorkflow) as Workflow;
        }
      }
    }
  }

  createDraft() {
    let draft = this.template != null ? JSON.parse(JSON.stringify(this.template)) : new Template();
    draft.author = this.currentUser;
    if (this.workflow != null) {
      draft.workflowId = this.workflow.id;
      draft.workflowName = this.workflow.name;
      if (this.workflow.type == "QC" || this.workflow.type == "FORMAT") {
        draft.isPublic = true;
      }
    }
    this.draftTemplate = draft;
    localStorage.setItem('draftTemplate', JSON.stringify(this.draftTemplate));
    if (this.template != null) {
      localStorage.setItem('template', JSON.stringify(this.template));
    }
    if (this.workflow != null) {
      localStorage.setItem('workflow', JSON.stringify(this.workflow));
    }
  }

  createTemplate(templateName?: string, isPublic?: boolean, lockValues?: boolean) {
    this.workflowLoading = true;
    this.hideChanges();
    let newTemplate = JSON.parse(JSON.stringify(this.draftTemplate));
    if (this.template != null && this.template.id != null && this.template.submittedDate != null) {
      if (newTemplate.data["_versions"] == null) { newTemplate.data["_versions"] = [] }
      newTemplate.data["_versions"].push({
        id: this.template.id,
        name: this.template.name,
        createdDate: this.template.createdDate,
        createdUser: this.template.author,
        submittedDate: this.template.submittedDate,
        submittedUser: this.template.submittedUser
      })
    }
    newTemplate.name = templateName;
    newTemplate.isPublic = isPublic;
    newTemplate.lockValues = lockValues;
    let templateType = (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT') ? "Report" : "Template";
    this.templatesService.createTemplate(newTemplate).subscribe(
      (template) => {
        this.template = template;
        localStorage.setItem('template', JSON.stringify(this.template));
        this.removeDraft();
        this.createDraft();
        this.workflowLoading = false;
        this.ngZone.run(() => {this.showSuccessDialog(templateType + " Successfully Created")})
        this.updateRoute();
      },
      (error) => {
        this.displayTemplateError(error, "Create");
        this.workflowLoading = false;
      }
    )
  }

  autosave() {
    this.updateTemplate({
      success: () => { },
      error: () => { }
    })
  }

  updateTemplate(callbacks: {success?: any, error?: any, finish?: any} = {}) {
    if (this.templateSaving) { return }
    this.templateSaving = true;
    if (this.autosaveTimeout != null) {
      clearTimeout(this.autosaveTimeout);
      this.autosaveTimeout = null;
    }
    let updatedTemplate = JSON.parse(JSON.stringify(this.draftTemplate));
    let templateType = (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT') ? "Report" : "Template";
    this.templatesService.updateTemplate(updatedTemplate).subscribe(
      (template) => {
        let favoriteIndex = this.favoriteTemplateIds.indexOf(template.id);
        if (favoriteIndex != -1) {
          template.isFavorite = true;
        }
        this.template = template;
        localStorage.setItem('template', JSON.stringify(this.template));
        this.resetDirty(template.data);
        let existingTemplate = this.templates.find((t) => t.id === template.id)
        if (existingTemplate != null) {
          let index = this.templates.indexOf(existingTemplate);
          this.templates[index] = template;
          this.usersService.updateProfileTemplates(this.templates);
        }
        if (this.template != null && this.workflow.type == 'QC' && this.autosaveTimeout == null && this.draftTemplate.dirty) {
          this.autosaveTimeout = setTimeout(() => this.autosave(), 30000);
        }
        if (callbacks.success != null) {
          callbacks.success()
        } else {
          this.ngZone.run(() => {this.showSuccessDialog(templateType + " Successfully Updated")})
        }
        if (callbacks.finish != null) {
          callbacks.finish()
        } else {
          this.templateSaving = false;
          this.workflowLoading = false;
        }
      },
      (error) => {
        if (callbacks.error != null) {
          callbacks.error(error);
        } else {
          this.displayTemplateError(error, "Update")
        }
        if (callbacks.finish != null) {
          callbacks.finish()
        } else {
          this.templateSaving = false;
          this.workflowLoading = false;
        }
      }
    )
  }

  displayTemplateError(error: {code: number, message: string}, action: string) {
    let templateType = this.workflow != null && (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT') ? "Report" : "Template";
    let message: string;
    if (error.code == 403) {
      message = "You don't have permission to " + action + " this " + templateType
    } else if (error.message == "name_conflict") {
      message = "A " + templateType + " with the same name already exists"
    } else {
      message = "Failed to " + action + " " + templateType
    }
    this.ngZone.run(() => {this.showErrorDialog(message)})
  }

  renameTemplate(name: string) {
    this.workflowLoading = true;
    this.hideChanges();
    let template = JSON.parse(JSON.stringify(this.template));
    template.name = name;
    let templateType = (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT') ? "Report" : "Template";
    this.templatesService.updateTemplate(template).subscribe(
      (template) => {
        this.template.name = name;
        localStorage.setItem('template', JSON.stringify(this.template));
        this.draftTemplate.name = name;
        localStorage.setItem('draftTemplate', JSON.stringify(this.draftTemplate));
        let existingTemplate = this.templates.find((t) => t.id === template.id)
        if (existingTemplate != null) {
          let index = this.templates.indexOf(existingTemplate);
          this.templates[index].name = name;
          this.usersService.updateProfileTemplates(this.templates);
        }
        this.workflowLoading = false;
        this.ngZone.run(() => {this.showSuccessDialog(templateType + " Successfully Renamed")})
      },
      (error) => {
        this.workflowLoading = false;
        let message: string;
        if (error.message == "name_conflict") {
          message = "A " + templateType + " with the same name already exists"
        } else {
          message = "Failed to Rename " + templateType
        }
        this.ngZone.run(() => {this.showErrorDialog(message)})
      }
    )
  }

  removeDraft() {
    localStorage.removeItem('draftTemplate');
    localStorage.removeItem('template')
    localStorage.removeItem('workflow');
    this.draftTemplate = null;
  }

  onSave() {
    if (this.template != null) {
      this.workflowLoading = true;
      this.hideChanges();
      this.updateTemplate();
    } else {
      this.ngZone.run(() => {this.showSaveAsDialog(false)})
    }
  }

  onSaveCopy() {
    this.ngZone.run(() => {this.showSaveAsDialog(true)})
  }

  onCreateTemplate() {
    this.ngZone.run(() => {this.showSaveDialog()})
  }

  onUpdateTemplate() {
    this.workflowLoading = true;
    this.hideChanges();
    this.updateTemplate();
  }

  showSaveDialog() {
    let dialogRef = this.dialog.open(SaveDialogComponent, {
      height: '240px',
      width: '320px',
      data: { }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.createTemplate(result.name, result.public, result.lock);
      }
    })
  }

  showSaveAsDialog(copy: boolean) {
    let dialogRef = this.dialog.open(SaveAsDialogComponent, {
      height: '240px',
      width: '320px',
      data: {
        copy: copy,
        templateName: (this.draftTemplate.name != null ? this.draftTemplate.name : this.workflow.name)
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.createTemplate(result.name);
      }
    })
  }

  showRenameDialog() {
    let dialogRef = this.dialog.open(RenameDialogComponent, {
      height: '240px',
      width: '320px',
      data: {templateName: this.template.name}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.renameTemplate(result.name);
      }
    })
  }

  onDownloadPDF() {
    if (this.template == null || this.pdfSaving) { return }
    this.pdfSaving = true
    if (this.draftTemplate.dirty) {
      this.updateTemplate({
        success: () => {
          this.downloadPDF();
          this.pdfSaving = false;
        },
        error: (error) => {
          this.displayTemplateError(error, "Update");
          this.pdfSaving = false;
        }
      })
    } else {
      this.downloadPDF();
      this.pdfSaving = false;
    }
  }

  downloadPDF() {
    if (this.template == null || this.pdfLoading) { return }
    this.pdfLoading = true
    if (this.pdfSubscription != null) { this.pdfSubscription.unsubscribe() }
    this.pdfSubscription = this.templatesService.downloadPdf(this.template.id).subscribe(
      (result) => {
        let fileName = this.template.name+'.pdf';
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          // For internet explorer file save
          window.navigator.msSaveOrOpenBlob(result, fileName);
        } else {
          // For all other browsers
          let element = this.pdfLink.nativeElement;
          element.href = URL.createObjectURL(result);
          element.download = fileName;
          element.removeEventListener("click", this.downloadPDF)
          element.click();
          element.addEventListener("click", this.downloadPDF)
        }
        this.pdfLoading = false;
      },
      (error) => {
        this.pdfLoading = false
        this.ngZone.run(() => {this.showErrorDialog("PDF Failed to Download")})
      }
    )
  }

  showSendEmailDialog() {
    let dialogRef = this.dialog.open(SendEmailDialogComponent, {
      height: '240px',
      width: '320px',
      data: {workflowId: this.workflowId}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null) {
        this.onSendEmail(result.recipients);
      }
    })
  }

  onSendEmail(recipients) {
    this.sending = true
    if (this.draftTemplate.dirty) {
      this.updateTemplate({
        success: () => this.sendEmail(recipients),
        error: (error) => {
          this.displayTemplateError(error, "Update");
          this.sending = false;
        }
      })
    } else {
      this.sendEmail(recipients);
    }
  }

  sendEmail(recipients) {
    if (this.template == null) { return }
    if (this.sendingSubscription != null) { this.sendingSubscription.unsubscribe() }
    this.sendingSubscription = this.templatesService.sendEmail(this.template.id, recipients).subscribe(
      (result) => {
        this.sending = false;
        this.ngZone.run(() => {this.showSuccessDialog("Email Successfully Sent")})
      },
      (error) => {
        this.sending = false;
        this.ngZone.run(() => {this.showErrorDialog("Email Failed to Send")})
      }
    )
  }

  loadWorkflowNavigation() {
    let navigation = [];
    let index = 0;
    for (let section of this.workflow.sections) {
      if (section.subsections) {
        let subindex = 0
        for (let subsection of section.subsections) {
          navigation.push({
            name: (subsection.navigationName || subsection.name),
            index: index,
            subindex: subindex
          })
          subindex += 1;
        }
      } else {
        navigation.push({
          name: (section.navigationName || section.name),
          index: index
        })
      }
      index += 1;
    }
    this.navigationSections = navigation;
    if (this.workflow.type === 'QC' || this.workflow.type === 'FORMAT') {
      this.canNavigate.emit({enabled: true, action: () => this.showWorkflowNavigation()})
    }
  }

  showWorkflowNavigation() {
    let dialogRef = this.dialog.open(WorkflowNavigationComponent, {
      position: {
        left: "12px"
      },
      height: (window.innerHeight-48)+'px',
      width: '320px',
      data: {sections: this.navigationSections}
    });
    dialogRef.afterClosed().subscribe((section) => {
      if (section != null) {
        this.navigateToSection(section);
      }
    })
  }

  navigateToSection(section) {
    let instance = this.sections[section.index].instance;
    if (section.subindex != null) {
      instance.subsections[section.subindex].navigateTo();
    } else {
      instance.navigateTo();
    }
    let offset = window.pageYOffset;
    let maxY = document.body.scrollHeight - window.innerHeight;
    if (offset < maxY) {
      let padding = 16;
      let headerHeight = !this.headerHidden ? 152 : 0;
      window.scroll(0, offset-padding-headerHeight);
    }
    this.resetSticky();
  }

  onScroll() {
    if (window.pageYOffset > 400) {
      this.headerState = "active"
    } else {
      this.headerState = "inactive"
    }
    this.updateSticky();
  }

  loadHeader() {
    if (this.workflow.header) {
      this.header = this.workflow.header;
      this.headerValues = {};
      for (let row of this.header.rows) {
        for (let field of row) {
          this.headerValues[field.id] = '';
        }
      }
      if (this.header.left != null) {
        this.headerValues[this.header.left.id] = '';
      }
      if (this.header.right != null) {
        this.headerValues[this.header.right.id] = '';
      }
    } else {
      if (this.headerHidden) { this.toggleHeader() }
    }
  }

  loadPreviousVersion() {
    if (this.previousVersion == null) { return }
    if (this.previousVersionSubscription) { this.previousVersionSubscription.unsubscribe() }
    this.previousVersionLoading = true;
    this.previousVersionSubscription = this.templatesService.getTemplate(this.previousVersion.id).subscribe(
      (template) => {
        this.previousTemplate = template;
        this.viewChanges();
        this.previousVersionLoading = false;
      },
      (error) => {
        this.previousVersionLoading = false;
        this.ngZone.run(() => {this.showErrorDialog("Failed to load version")})
      }
    )
  }

  viewChanges() {
    this.viewingChanges = true;
    for (let section of this.sections) {
      section.instance.setPreviousValue(this.previousTemplate.data);
    }
  }

  hideChanges() {
    this.viewingChanges = false;
    for (let section of this.sections) {
      section.instance.resetPreviousValue();
    }
    if (this.dynamicOverlays != null) {
      this.dynamicOverlays.viewContainerRef.clear();
    }
  }

  onUndoableAction(action: UndoAction) {
    this.undoActions.push(action);
    this.showUndoDialog(action);
  }

  undo() {
    this.closeUndoDialog();
    if (this.undoActions.length > 0) {
      let action = this.undoActions.pop();
      action.field.undo(action);
    }
  }

  showUndoDialog(action: UndoAction) {
    this.undoDescription = action.description;
    setTimeout(() => this.undoDialogState = "open", 1);
    if (this.undoOverlayRef == null) {
      let config = new OverlayConfig();
      config.positionStrategy = this.overlay.position().global().bottom('24px').right('24px');
      config.scrollStrategy = this.overlay.scrollStrategies.noop();
      this.undoOverlayRef = this.overlay.create(config);
      this.undoOverlayRef.attach(this.undoTemplate);
    }
    if (this.undoDialogTimeout != null) { clearTimeout(this.undoDialogTimeout) }
    this.undoDialogTimeout = setTimeout(() => this.closeUndoDialog(), 6000);
  }

  closeUndoDialog(undo?: boolean) {
    this.undoDialogState = "closed";
    if (undo) {
      this.undo();
    }
  }

  onUndoFadeDone(event) {
    if (event.fromState == "open" && event.toState == "closed") {
      this.removeUndoOverlay();
    }
  }

  removeUndoOverlay() {
    if (this.undoOverlayRef != null) {
      this.undoOverlayRef.dispose();
      this.undoOverlayRef = null;
    }
  }

  setLockValues(state) {
    for (let section of this.sections) {
      section.instance.setLockValues(state);
    }
  }

  toggleLockValues() {
    if (this.draftTemplate != null) {
      let state = !this.draftTemplate.lockValues;
      this.draftTemplate.lockValues = state;
      this.setLockValues(state);
    }
  }

  toggleIsPublic() {
    if (this.draftTemplate != null) {
      this.draftTemplate.isPublic = !this.draftTemplate.isPublic;
    }
  }

  toggleIsArchived() {
    if (this.archivingTemplate) { return }
    this.archivingTemplate = true
    let isArchived = !this.template.isArchived
    this.templatesService.archiveTemplate(this.template, isArchived).subscribe(
      (result) => {
        this.template.isArchived = isArchived;
        this.archivingTemplate = false
      },
      (error) => {
        this.archivingTemplate = false
        let action = isArchived ? "archive" : "unarchive";
        this.showErrorDialog("Failed to " + action + " template")
      }
    )
  }

  resetTransfer() {
    this.transfer = new Transfer({
      workflowId: this.workflow.id,
      workflowType: this.workflow.type,
      templateId: (this.template != null ? this.template.id : null)
    });
  }

  resetFiles() {
    for (let section of this.sections) {
      section.instance.resetFiles();
    }
  }

  sendAnother() {
    this.workflowLoading = true;
    this.resetFiles();
    this.resetTransfer();
    this.workflowLoading = false;
  }

  resetSticky() {
    for (let header of this.stickyHeaders) {
      header.reset();
    }
  }

  updateSticky() {
    for (let header of this.stickyHeaders) {
      header.update();
    }
  }

  toggleHeader() {
    this.headerHidden = !this.headerHidden;
    this.headerHiddenChanged.emit({hidden: this.headerHidden});
    this.setHeaderHeight();
  }

  setHeaderHeight() {
    let headerHeight = !this.headerHidden ? 152 : 0;
    for (let header of this.stickyHeaders) {
      header.offset = headerHeight;
      header.reset();
    }
  }

  onAtLeastOneRequiredFieldAdded(event: {id: string, field: FieldComponent}) {
    this.atLeastOneRequiredFields[event.id] = event.field;
  }

  validate() {
    if (this.workflow.type == "QC") {
      this.valid = true
    } else {
      this.valid = this.sections.length > 0 &&
        this.sections.filter((s) => !s.instance.valid).length == 0
    }
    this.errors = {}
    if (this.validations != null) {
      if (this.validations.atLeastOneRequired != null) {
        let atLeastOne = false
        for (let group of this.validations.atLeastOneRequired) {
          if (group.length < 1) { continue }
          let blankFields = group.filter((x) => {
            let field = this.atLeastOneRequiredFields[x]
            return field == null || field.blank
          })
          if (blankFields.length == 0) { atLeastOne = true }
        }
        if (!atLeastOne) {
          this.valid = false;
          this.errors['atLeastOneRequired'] = this.validations.atLeastOneRequired
        }
      }
    }
  }

  showErrors() {
    if (this.template == null || this.valid || this.errorDescriptions.length == 0) { return }
    let config = new OverlayConfig();
    config.positionStrategy = this.overlay.position().connectedTo(
      this.submitButtonBadge,
      {originX: 'end', originY: 'bottom'},
      {overlayX: 'start', overlayY: 'bottom'}
    );
    config.scrollStrategy = this.overlay.scrollStrategies.close();
    this.errorsOverlayRef = this.overlay.create(config);
    this.errorsOverlayRef.attach(this.errorsTemplate);
  }

  hideErrors() {
    if (this.errorsOverlayRef != null) {this.errorsOverlayRef.dispose()};
  }

  descriptionForError(key, value): string {
    switch (key) {
    case 'atLeastOneRequired':
      let i = 1;
      let groups = value.map((g) => {
        let string = '<li>'+g.map((f) => {
          let field = this.atLeastOneRequiredFields[f]
          return (field != null ? field.label : f)
        }).join('/')+'</li>'
        i += 1
        return string
      })
      return 'Workflow requires at least one of the following:<ul>' + groups.join('') + '</ul>';
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/transfers/new-transfer.component.ts